import type { ExerciseLog, SessionLog } from './sessionStorage';
import { computeSetVolume } from './volumeTracking';

/**
 * Training Load (Fatigue) Model
 *
 * Goal: represent *fatigue-relevant* workload across mixed modalities:
 * - Strength: tonnage (weight × reps)
 * - Ruck/Carry: external load × distance (meters), normalized
 * - Cardio: distance/time based load (meters / seconds), normalized
 * - Circuits/AMRAP: rounds/time based load, normalized
 * - Bodyweight: reps-based load, normalized
 *
 * IMPORTANT:
 * - `ExerciseLog.actualDistance` is stored in **meters**
 * - This is *not* "lbs lifted". It's a unified "load units" metric for fatigue.
 */

const DEFAULT_RPE = 7;

function parseTimeToSeconds(time?: string): number {
  if (!time) return 0;
  const parts = time.split(':').map(p => p.trim()).filter(Boolean);
  if (parts.length === 0) return 0;
  const nums = parts.map(p => Number(p));
  if (nums.some(n => Number.isNaN(n))) return 0;

  // Support MM:SS and HH:MM:SS
  if (nums.length === 2) {
    const [mm, ss] = nums;
    return Math.max(0, mm * 60 + ss);
  }
  if (nums.length === 3) {
    const [hh, mm, ss] = nums;
    return Math.max(0, hh * 3600 + mm * 60 + ss);
  }
  return 0;
}

function getExerciseRPE(ex: ExerciseLog): number {
  // Per-set tracking (strength)
  if (ex.sets && ex.sets.length > 0) {
    const rpes = ex.sets.map(s => s.rpe).filter((r): r is number => typeof r === 'number' && r > 0);
    if (rpes.length > 0) {
      return rpes.reduce((a, b) => a + b, 0) / rpes.length;
    }
  }

  // Circuit rounds
  if (ex.circuitSets && ex.circuitSets.length > 0) {
    const rpes = ex.circuitSets.map(s => s.rpe).filter((r): r is number => typeof r === 'number' && r > 0);
    if (rpes.length > 0) {
      return rpes.reduce((a, b) => a + b, 0) / rpes.length;
    }
  }

  // Swim sets
  if (ex.swimSets && ex.swimSets.length > 0) {
    const rpes = ex.swimSets.map(s => s.rpe).filter((r): r is number => typeof r === 'number' && r > 0);
    if (rpes.length > 0) {
      return rpes.reduce((a, b) => a + b, 0) / rpes.length;
    }
  }

  // Legacy single-value
  if (typeof ex.actualRPE === 'number' && ex.actualRPE > 0) return ex.actualRPE;

  return DEFAULT_RPE;
}

function getRpeFactor(ex: ExerciseLog): number {
  const rpe = getExerciseRPE(ex);
  // Clamp to reasonable
  const clamped = Math.min(10, Math.max(1, rpe));
  return clamped / 10;
}

function estimateStrengthTonnage(ex: ExerciseLog): number {
  let tonnage = 0;

  // Superset rounds
  if ((ex as any).supersetRounds && Array.isArray((ex as any).supersetRounds)) {
    for (const round of (ex as any).supersetRounds) {
      if (!round?.exercises || !Array.isArray(round.exercises)) continue;
      for (const sub of round.exercises) {
        if (sub?.weight > 0 && sub?.reps > 0) tonnage += computeSetVolume(sub, sub.name || ex.exerciseName);
      }
    }
    return tonnage;
  }

  // Per-set
  if (ex.sets && ex.sets.length > 0) {
    for (const set of ex.sets) {
      if ((set.weight || 0) > 0 && (set.reps || 0) > 0) {
        tonnage += computeSetVolume(set, ex.exerciseName);
      }
    }
    return tonnage;
  }

  // Legacy single-value
  if (ex.actualLoad && ex.actualReps) {
    const setsMatch = ex.targetSets?.match(/^(\d+)/);
    const sets = setsMatch ? parseInt(setsMatch[1], 10) : 1;
    tonnage += ex.actualLoad * ex.actualReps * Math.max(1, sets);
  }

  return tonnage;
}

function estimateCarryLoad(ex: ExerciseLog): number {
  // Uses the same normalization used in volumeTracking.ts
  // Volume = (totalWeight lbs × distance meters) / 10
  if (!ex.actualLoad || !ex.actualDistance) return 0;
  const plateCarrierWeight = (ex as any).plateCarrierWeight || 0;
  const totalWeight = ex.actualLoad + plateCarrierWeight;
  return (totalWeight * ex.actualDistance) / 10;
}

function estimateCardioLoad(ex: ExerciseLog): number {
  // For unloaded cardio, count distance/time so it impacts fatigue.
  // NOTE: actualDistance is meters.
  const distanceM = ex.actualDistance || 0;
  const seconds = parseTimeToSeconds(ex.actualTime);

  // Distance-driven load (10k meters ~ 50k load units)
  const distanceLoad = distanceM > 0 ? distanceM * 5 : 0;

  // Time-driven load (10 minutes ~ 30k load units)
  const timeLoad = seconds > 0 ? seconds * 50 : 0;

  return Math.max(distanceLoad, timeLoad);
}

function estimateCircuitLoad(ex: ExerciseLog): number {
  const rounds = ex.actualRounds || (ex.circuitSets?.length || 0);
  if (rounds > 0) {
    // 6 rounds ~ 30k load units
    return rounds * 5000;
  }
  const seconds = parseTimeToSeconds(ex.actualTime);
  return seconds > 0 ? seconds * 50 : 0;
}

function estimateBodyweightLoad(ex: ExerciseLog): number {
  const reps = ex.actualReps || 0;
  if (reps <= 0) return 0;
  // 100 reps ~ 10k load units
  return reps * 100;
}

function estimateSwimLoad(ex: ExerciseLog): number {
  if (ex.swimSets && ex.swimSets.length > 0) {
    const meters = ex.swimSets.reduce((sum, s) => sum + (s.distance || 0), 0);
    return meters * 5;
  }
  if (ex.actualDistance) return ex.actualDistance * 5;
  return 0;
}

export function calculateExerciseTrainingLoad(ex: ExerciseLog): number {
  const rpeFactor = getRpeFactor(ex);

  // Strength (weight × reps)
  const tonnage = estimateStrengthTonnage(ex);
  if (tonnage > 0) return tonnage * rpeFactor;

  // Loaded carries / rucks (prefer explicit types)
  if (ex.exerciseType === 'ruck' || ex.exerciseType === 'carry') {
    return estimateCarryLoad(ex) * rpeFactor;
  }

  // Cardio: if it has external load + distance, treat as carry-like
  if (ex.exerciseType === 'cardio' && ex.actualLoad && ex.actualDistance) {
    return estimateCarryLoad(ex) * rpeFactor;
  }

  // Cardio (unloaded)
  if (ex.exerciseType === 'cardio') {
    return estimateCardioLoad(ex) * rpeFactor;
  }

  // Timed (unloaded)
  if (ex.exerciseType === 'timed') {
    const seconds = parseTimeToSeconds(ex.actualTime);
    return (seconds * 50) * rpeFactor;
  }

  // Circuits / AMRAP / EMOM - check for actualRounds regardless of exerciseType
  // This handles exercises in multi-exercise circuit blocks (Grinder circuits, AMRAPs)
  // where exercises are typed as 'bodyweight' or 'cardio' but tracked via rounds
  if (ex.exerciseType === 'circuit' || ex.actualRounds) {
    return estimateCircuitLoad(ex) * rpeFactor;
  }

  // Bodyweight (only if no rounds - otherwise handled above as circuit)
  if (ex.exerciseType === 'bodyweight') {
    return estimateBodyweightLoad(ex) * rpeFactor;
  }

  // Swim intervals
  if (ex.exerciseType === 'swim-sets') {
    return estimateSwimLoad(ex) * rpeFactor;
  }

  return 0;
}

export function calculateExercisesTrainingLoad(exercises: ExerciseLog[] = []): number {
  return exercises.reduce((sum, ex) => sum + calculateExerciseTrainingLoad(ex), 0);
}

export function calculateSessionTrainingLoad(session: SessionLog): number {
  if (session.sessionType === 'range') {
    // Keep existing convention: range sessions contribute a fixed load
    return 2000;
  }
  // PT / assessment / sprint all share the exercises model
  const exercises = (session as any).exercises as ExerciseLog[] | undefined;
  return calculateExercisesTrainingLoad(exercises || []);
}


